import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { openModalAction } from '../../actions/modal';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/PageHeader/Button';
import { TextInput } from '../../components/PageHeader/TextInput';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import Strings from '../../Strings';
import { lastActivityDisplay, showStatus } from '../../utils';
import { EDIT_USER, INVITE_USER, userActions } from '../../actions/user';
import { CAP_MANAGER, CARE_PROVIDER, DOCTOR, ORGANIZATION_MANAGER } from '../../utils/userRoles';
import { HEADER_BUTTON_BLUE } from '../../constants';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';

function UserList(props) {
  const orgId = props.organizationId ? decodeURIComponent(props.organizationId) : props.orgId;
  const pageRequest = React.useRef({
    offset: 0,
    search: '',
    orgId,
    care_providers: props.type === CARE_PROVIDER ? true : undefined,
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [pagination, setPagination] = React.useState({});
  const { role, refreshTimestamp } = props;
  const canEdit = (role === DOCTOR && hasPermission(PERMISSIONS.ORGANIZATION_ADMIN_EDIT))
    || (role === CAP_MANAGER && hasPermission(PERMISSIONS.ORGANIZATION_CAPMANAGER_EDIT))
    || (role === ORGANIZATION_MANAGER && hasPermission(PERMISSIONS.ORGANIZATION_ORG_MANAGER_EDIT));

  const loadData = () => {
    setIsLoading(true);
    props.getUsers(pageRequest.current).then(response => {
      if (response && response.response.data) {
        setUsers(response.response.data);
        setPagination(response.response.pagination);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    pageRequest.current.archetype = role;
    loadData();
  }, [role, refreshTimestamp]);

  const onSearchQueryChange = query => {
    return onSearchQueryChangeDebounced(query);
  };

  const onSearchQueryChangeDebounced = _.debounce(query => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    loadData();
  }, 1000);

  const addUser = () => {
    props.addUser(loadData);
  };

  const editUser = (id, user) => {
    props.editUser(user, loadData);
  };

  const deleteUser = (id, user) => {
    const idToDelete = user.masked_id ? user.masked_id : user.id;
    const data = {
      title: (
        <span>
          {Strings.deleteOrgAdminWarning} <b>{`${user.first_name} ${user.last_name}`}</b>?
        </span>
      ),

      onConfirmAction: userActions.delete(idToDelete),
      confirmPostAction: () => loadData(),
      onCancelAction: null,
    };
    props.openConfirmModal(data);
  };

  const resetPassword = user => {
    const data = {
      title: (
        <span>
          {Strings.warnigs.adminResetPassword} <b>{user.username}</b>?
        </span>
      ),
      hideCaution: true,
      onConfirmAction: userActions.resetPassword({
        method: 'email',
        email: user.email,
      }),
      onCancelAction: null,
      caption: Strings.confirmPasswordReset,
    };

    props.openConfirmModal(data);
  };

  const resendInvite = user => {
    const data = {
      title: (
        <span>
          {Strings.warnigs.resendInvite} <b>{user.username}</b>?
        </span>
      ),
      hideCaution: true,
      onConfirmAction: userActions.resendInvite({
        id: user.masked_id ? user.masked_id : user.id,
      }),
      onCancelAction: null,
      caption: Strings.confirmReinvite,
    };

    props.openConfirmModal(data);
  };

  const onTextInputChange = e => {
    onSearchQueryChange(e.target.value);
  };

  const getHeaderComponents = () => {
    return (
      <>
        <TextInput class="search" placeholder={Strings.search} onChange={onTextInputChange} />
        {props.type !== CARE_PROVIDER && (
          <Button onClick={addUser} class={HEADER_BUTTON_BLUE} title={Strings.add} />
        )}
      </>
    );
  };

  const roleLabel = props.headerLabel || (props.type === CARE_PROVIDER ? Strings.capPatient.careProviders : Strings.rolesPluralEnum[role]);
  const orgPrefix = props.organizationName ? `${props.organizationName}: ` : '';
  const headerTitle =
    !isLoading && users && pagination
      ? orgPrefix + Strings.formatString(
          Strings.showingXOf,
          users.length,
          pagination.totalRecords ? pagination.totalRecords : 0,
          roleLabel,
        )
      : orgPrefix + Strings.showingWait;

  const columns = [
    columnHelper.accessor('name', {
      id: Strings.name,
      header: Strings.name,
      cell: info => info.getValue() || '-',
      accessorFn: row => `${row.first_name} ${row.last_name}`,
    }),
    columnHelper.accessor('username', {
      header: Strings.userName,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('email', {
      header: Strings.emailAddress,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('phone', {
      header: Strings.phoneNumber,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('last_login', {
      header: Strings.lastLogin,
      cell: info => lastActivityDisplay(info.getValue()),
      sortingFn: 'dateSortingFn',
      sortUndefined: -1,
    }),
  ];

  if (props.type === CARE_PROVIDER) {
    columns.push(
      columnHelper.accessor('organization', {
        header: Strings.organization,
        cell: info => info.getValue() || '-',
      })
    );
  }

  if (role === DOCTOR) {
    columns.push(
      columnHelper.accessor('read_only', {
        header: Strings.adminWritePrevileges,
        cell: info => showStatus(info.getValue() ? 'disabled' : 'enabled'),
      })
    );

    columns.push(
      columnHelper.accessor('timer_off', {
        header: Strings.adminTimerRights,
        cell: info => showStatus(info.getValue() ? 'disabled' : 'enabled'),
      })
    );
  }

  columns.push(
    columnHelper.accessor('status', {
      header: Strings.status,
      cell: info => showStatus(info.getValue()),
    })
  );

  columns.push(
    columnHelper.accessor('anonymize', {
      header: Strings.maskPHIData,
      cell: info => showStatus(info.getValue() ? 'enabled' : 'disabled'),
    })
  );

  let buttons = [];
  const adminButtons = [
    {
      icon: 'edit',
      onClick: editUser,
      text: Strings.edit,
    },
    {
      icon: 'delete',
      onClick: deleteUser,
      text: Strings.delete,
    },
    {
      icon: 'refresh',
      onClick: (_, user) => user.status === 'pending' ? resendInvite(user) : resetPassword(user),
      text: (_, user) => user.status === 'pending' ? Strings.reInviteToPortal : Strings.resetPassword,
    },
  ];
  if (props.type === CARE_PROVIDER && hasPermission(PERMISSIONS.ORGANIZATION_PROVIDER_REASSIGN)) {
    buttons.push({
      icon: 'refresh',
      onClick: (_, user) => props.reassignCareProvider({user, organizationId: orgId}),
      text: Strings.reassignCareProvider,
    });
  } 
  if (props.type !== CARE_PROVIDER && canEdit  ) {
    buttons = adminButtons;
  }

  return (
    <>
      <PageHeader left={headerTitle} right={getHeaderComponents()} />
      <Table3
        isLoading={isLoading}
        name="organizations"
        data={users}
        columns={columns}
        buttons={buttons}
      />
    </>
  );
}

const mapStateToProps = state => {
  return {
    orgId: state.auth.organization?.masked_id,
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  onNavigate: path => dispatch(push(path)),
  getUsers: pageRequest => dispatch(userActions.list(pageRequest)),
  addUser: onSuccess =>
    dispatch(
      openModalAction('edit-user', {
        action: userActions.invite,
        actionType: INVITE_USER,
        organization_id: props.organizationId,
        userType: props.role,
        onSuccess,
      }),
    ),
  editUser: (user, onSuccess) =>
    dispatch(
      openModalAction('edit-user', {
        action: userActions.edit,
        actionType: EDIT_USER,
        organization_id: props.organizationId,
        user,
        userType: props.role,
        onSuccess,
      }),
    ),
  reassignCareProvider: data =>
    dispatch(
      openModalAction('reassign-care-provider', data),
    ),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserList);
